import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';

interface ServingsStepperProps {
  servings: number;
  onChange: (delta: number) => void;
  /** Optional derived yield label, e.g. "≈ 1,2 kg · 8 bols". Rendered only if present. */
  yieldLabel?: string;
  variant?: 'full' | 'slim';
  min?: number;
  max?: number;
}

export const ServingsStepper: React.FC<ServingsStepperProps> = ({
  servings,
  onChange,
  yieldLabel,
  variant = 'full',
  min = 1,
  max = 20,
}) => {
  const { t } = useTranslation();
  const canDecrease = servings > min;
  const canIncrease = servings < max;

  const StepButton = ({ dir }: { dir: -1 | 1 }) => {
    const enabled = dir === -1 ? canDecrease : canIncrease;
    return (
      <TouchableOpacity
        style={[styles.stepButton, !enabled && styles.stepButtonDisabled]}
        onPress={() => enabled && onChange(dir)}
        disabled={!enabled}
        activeOpacity={0.7}
        hitSlop={4}
      >
        <Feather
          name={dir === -1 ? 'minus' : 'plus'}
          size={16}
          color={enabled ? Paprika.textPrimary : Paprika.textMuted}
        />
      </TouchableOpacity>
    );
  };

  if (variant === 'slim') {
    return (
      <View style={styles.slim}>
        <Text style={styles.slimLabel}>{t('recipeDetail.servings', 'PORTIONS')}</Text>
        <StepButton dir={-1} />
        <Text style={styles.slimNumber}>{servings}</Text>
        <StepButton dir={1} />
        {yieldLabel ? (
          <>
            <View style={styles.slimDivider} />
            <Text style={styles.slimYield} numberOfLines={1}>{yieldLabel}</Text>
          </>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardLeft}>
        <Text style={styles.cardLabel}>{t('recipeDetail.servings', 'PORTIONS')}</Text>
        {yieldLabel ? <Text style={styles.cardYield} numberOfLines={1}>{yieldLabel}</Text> : null}
      </View>
      <View style={styles.cardRight}>
        <StepButton dir={-1} />
        <Text style={styles.cardNumber}>{servings}</Text>
        <StepButton dir={1} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // full card
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    paddingVertical: PaprikaSpacing.sm + 2,
    paddingHorizontal: PaprikaSpacing.md,
  },
  cardLeft: {
    gap: 2,
    flexShrink: 1,
  },
  cardLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
  },
  cardYield: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  cardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
  },
  cardNumber: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 23,
    color: Paprika.textPrimary,
    minWidth: 24,
    textAlign: 'center',
  },
  // slim pill
  slim: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.full,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    paddingVertical: 5,
    paddingHorizontal: PaprikaSpacing.sm + 2,
  },
  slimLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
  },
  slimNumber: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: Paprika.textPrimary,
    minWidth: 20,
    textAlign: 'center',
  },
  slimDivider: {
    width: 1,
    height: 20,
    backgroundColor: Paprika.borderLight,
  },
  slimYield: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    flexShrink: 1,
  },
  // shared step button
  stepButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Paprika.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.cardBg,
  },
  stepButtonDisabled: {
    borderColor: Paprika.borderLight,
    backgroundColor: Paprika.surfaceLow,
  },
});

export default ServingsStepper;
